import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useSearchParams } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import theme from './theme';
import Home from './pages/Home';
import Login from './pages/Login';
import Register from './pages/Register';
import Quotations from './pages/Quotations';
import Dashboard from './pages/Dashboard';
import ServiceSchedulePage from './pages/ServiceSchedulePage';
import ResidentialCleaningPage from './pages/ResidentialCleaningPage';
import VacationRentals from './pages/VacationRentals';
import HospitalityLinenPage from './pages/HospitalityLinenPage';
import BookingSuccess from './pages/BookingSuccess';
import UserBookings from './pages/UserBookings';
import BookingDetails from './pages/BookingDetails';
import RescheduleBooking from './pages/RescheduleBooking';
import Profile from './pages/Profile';
import CleaningTipsPage from './pages/CleaningTipsPage';
import ToolsAndProducts from './pages/ToolsAndProducts';
import { AuthProvider } from './contexts/AuthContext';
import { TenantProvider } from './contexts/TenantContext';
import { BookingProvider } from './contexts/BookingContext';
import { QuestionnaireProvider } from './contexts/QuestionnaireContext';
import { ServiceCatalogProvider } from './contexts/ServiceCatalogContext';
import UnifiedBookingFlow from './components/booking/UnifiedBookingFlow';
import HouseproBookingFlow from './components/booking/HouseproBookingFlow';
import MaidOnboardingWizard from './components/maid-onboarding/MaidOnboardingWizard';
import OnboardingSuccess from './pages/OnboardingSuccess';
import { isHouseproBookingEnabled } from './utils/featureFlags';
import MaidProfile from './pages/MaidProfile';
import TeamManagement from './pages/TeamManagement';
import AdminRoute from './components/auth/AdminRoute';
import MaidRoute from './components/auth/MaidRoute';
import MaidPortalPage from './pages/MaidPortalPage';
import MaidJobDetailPage from './pages/MaidJobDetailPage';
import ServiceCatalogPage from './pages/ServiceCatalog';
import UserManagement from './pages/UserManagement';
import CustomerManagement from './pages/CustomerManagement';
import ResetPasswordPage from './pages/ResetPasswordPage';
import CookieConsent from './components/CookieConsent';
import { initializeAnalytics } from './utils/analytics';
import BucketDocuments from './pages/BucketDocuments';
import MaidMapDisplay from './components/MaidMapDisplay';
import QuestionnaireLeads from './pages/QuestionnaireLeads';
import PropertyManagerLeads from './pages/PropertyManagerLeads';
import W9Campaign from './pages/W9Campaign';
import Landing from './pages/Landing';
import LuxuryCleaningCharter from './pages/LuxuryCleaningCharter';
import ServiceDetail from './pages/services/ServiceDetail';
import CityLandingPage from './pages/CityLandingPage';
import ServiceAreasHub from './pages/ServiceAreasHub';
import Analytics from './pages/Analytics';
import AdminJobsNearMaid from './pages/AdminJobsNearMaid';
import TaskTemplatesPage from './pages/TaskTemplatesPage';
import JobHistoryPage from './pages/JobHistoryPage';
import DocumentPage from './pages/DocumentPage';
import ChecklistPage from './pages/ChecklistPage';
import TechnicianAddressPage from './pages/TechnicianAddressPage';
import AdminQuotations from './pages/AdminQuotations';
import PaymentManagementPage from './pages/PaymentManagementPage';

// Component to handle homepage with onboarding parameter check
function HomePage() {
  const [searchParams] = useSearchParams();
  const showOnboarding = searchParams.get('onboarding') === 'true';
  
  if (showOnboarding) {
    return <MaidOnboardingWizard />;
  }
  
  return <Landing />;
}

function App() {
  // Initialize analytics on app start
  useEffect(() => {
    initializeAnalytics();
  }, []);

  return (
    <HelmetProvider>
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <TenantProvider>
        <AuthProvider>
          <ServiceCatalogProvider>
            <Router>
            <Routes>
              {/* Home page with onboarding parameter check */}
              <Route path="/" element={<HomePage />} />
              
              {/* Booking-related routes with BookingProvider */}
              <Route path="/quotations" element={
                <BookingProvider><Quotations /></BookingProvider>
              } />
              <Route path="/residential-cleaning" element={
                <BookingProvider><ResidentialCleaningPage /></BookingProvider>
              } />
              <Route path="/hospitality-linen" element={<HospitalityLinenPage />} />
              <Route path="/vacation-rentals" element={
                <BookingProvider><VacationRentals /></BookingProvider>
              } />
              <Route path="/booking-success" element={
                <BookingProvider><BookingSuccess /></BookingProvider>
              } />
              <Route path="/bookings" element={
                <BookingProvider><UserBookings /></BookingProvider>
              } />
              <Route path="/bookings/:bookingId" element={
                <BookingProvider><BookingDetails /></BookingProvider>
              } />
              <Route path="/bookings/:bookingId/reschedule" element={
                <BookingProvider><RescheduleBooking /></BookingProvider>
              } />
              
              {/* Conditional booking flow with questionnaire tracking */}
              <Route path="/booking" element={
                <QuestionnaireProvider>
                  <BookingProvider>
                    {isHouseproBookingEnabled() 
                      ? <HouseproBookingFlow /> 
                      : <UnifiedBookingFlow />}
                  </BookingProvider>
                </QuestionnaireProvider>
              } />
              <Route path="/housepro-booking" element={
                <QuestionnaireProvider>
                  <BookingProvider><HouseproBookingFlow /></BookingProvider>
                </QuestionnaireProvider>
              } />
              
              {/* Non-booking routes WITHOUT BookingProvider - DOMAIN ISOLATION */}
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
              <Route path="/profile" element={<Profile />} />
              <Route path="/maid-onboarding" element={<MaidOnboardingWizard />} />
              <Route path="/onboarding-success" element={<OnboardingSuccess />} />
              <Route path="/maid-portal" element={<MaidRoute><MaidPortalPage /></MaidRoute>} />
              <Route path="/maid-portal/jobs/:jobId" element={<MaidRoute><MaidJobDetailPage /></MaidRoute>} />
              {/* Technician is the canonical term — same pages, new URLs (old ones keep working) */}
              <Route path="/technician-portal" element={<MaidRoute><MaidPortalPage /></MaidRoute>} />
              <Route path="/technician-portal/jobs/:jobId" element={<MaidRoute><MaidJobDetailPage /></MaidRoute>} />
              <Route path="/dashboard" element={<AdminRoute><Dashboard /></AdminRoute>} />
              <Route path="/service-schedule" element={<AdminRoute><ServiceSchedulePage /></AdminRoute>} />
              <Route path="/cleaning-tips" element={<CleaningTipsPage />} />
              <Route path="/luxury-cleaning-charter" element={<LuxuryCleaningCharter />} />
              <Route path="/tools-and-products" element={<ToolsAndProducts />} />
              <Route path="/maid-profile" element={<AdminRoute><MaidProfile /></AdminRoute>} />
              <Route path="/team-management" element={<AdminRoute><TeamManagement /></AdminRoute>} />
              <Route path="/service-catalog" element={<AdminRoute><ServiceCatalogPage /></AdminRoute>} />
              <Route path="/user-management" element={<AdminRoute><UserManagement /></AdminRoute>} />
              <Route path="/customer-management" element={<AdminRoute><CustomerManagement /></AdminRoute>} />
              <Route path="/questionnaire-leads" element={<AdminRoute><QuestionnaireLeads /></AdminRoute>} />
              <Route path="/property-manager-leads" element={<AdminRoute><PropertyManagerLeads /></AdminRoute>} />
              <Route path="/w9-campaign" element={<AdminRoute><W9Campaign /></AdminRoute>} />
              <Route path="/bucket-documents" element={<AdminRoute><BucketDocuments /></AdminRoute>} />
              <Route path="/maid-map" element={<AdminRoute><MaidMapDisplay /></AdminRoute>} />
              <Route path="/analytics" element={<AdminRoute><Analytics /></AdminRoute>} />
              <Route path="/admin/jobs-near-maid" element={<AdminRoute><AdminJobsNearMaid /></AdminRoute>} />
              <Route path="/task-templates" element={<AdminRoute><TaskTemplatesPage /></AdminRoute>} />
              <Route path="/jobs" element={<AdminRoute><JobHistoryPage /></AdminRoute>} />
              {/* Former name of the Jobs page; kept so old links keep working */}
              <Route path="/job-history" element={<AdminRoute><JobHistoryPage /></AdminRoute>} />
              <Route path="/services/:slug" element={<ServiceDetail />} />
              {/* Public SEO landing pages: city service areas */}
              <Route path="/service-areas" element={<ServiceAreasHub />} />
              <Route path="/house-cleaning/:citySlug" element={<CityLandingPage />} />
              <Route path="/quote/:token" element={<DocumentPage />} />
              <Route path="/invoice/:token" element={<DocumentPage />} />
              <Route path="/checklist/:token" element={<ChecklistPage />} />
              <Route path="/technician/address/:token" element={<TechnicianAddressPage />} />
              <Route path="/admin/quotations" element={<AdminRoute><AdminQuotations /></AdminRoute>} />
              <Route path="/payment-management" element={<AdminRoute><PaymentManagementPage /></AdminRoute>} />
            </Routes>
            <CookieConsent />
            </Router>
          </ServiceCatalogProvider>
        </AuthProvider>
      </TenantProvider>
    </ThemeProvider>
    </HelmetProvider>
  );
}

export default App; 